import React from 'react';
import { toast } from 'react-toastify';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { request } from '@mairistem/tools';
import { Dimmer, Loader } from '@mairistem/composants';

import { baseUrlApi } from '@/routes';
import { useEntiteStore } from '@/zustand';

interface BlocGraphiqueStatistiqueProps {
  anneeMax: number;
  anneeMin: number;
}

// Ordre custom des éléments dans le tooltip
const tooltipOrder: Record<string, number> = {
  'PACS conclus': 1,
  'PACS modifiés': 2,
  'PACS dissous': 3,
  'PACS irrecevables': 4,
  Total: 5,
};

const getSeriesOrder = (seriesName: string) =>
  tooltipOrder[seriesName] ?? Number.MAX_SAFE_INTEGER;

const BlocGraphiqueStatistique = (props: BlocGraphiqueStatistiqueProps) => {
  const { anneeMin, anneeMax } = props;

  const [statistiques, setStatistiques] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const blocked = useEntiteStore((state) => state.blocked);

  React.useEffect(() => {
    if (!blocked) {
      setLoading(true);
      request
        .get(`${baseUrlApi}/pacs/statistiques/${anneeMin}/${anneeMax}`)
        .then((response: any) => {
          setStatistiques(response);
        })
        .catch(() => {
          setStatistiques(null);
          toast.error(
            `Impossible de charger les statistiques entre les années ${anneeMin} et ${anneeMax}`,
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [anneeMin, anneeMax, blocked]);

  const data = React.useMemo(() => {
    if (!statistiques || typeof statistiques !== 'object') {
      return [];
    }

    const years = Object.keys(statistiques).sort();
    return years.map((year) => {
      const yearData = statistiques[year];
      const conclus = yearData['PACS conclus'] || 0;
      const modifies = yearData['PACS modifiés'] || 0;
      const dissous = yearData['PACS dissous'] || 0;
      const irrecevables = yearData['PACS irrecevables'] || 0;
      const total = conclus + modifies + dissous + irrecevables;

      return {
        annee: year,
        conclusion: conclus,
        modification: modifies,
        dissous,
        irrecevable: irrecevables,
        total,
      };
    });
  }, [statistiques]);

  return (
    <div
      style={{
        marginLeft: 0,
        height: '100%',
      }}
    >
      {loading && (
        <Dimmer active inverted>
          <Loader content="Chargement des statistiques..." />
        </Dimmer>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="annee"
            tick={{ fill: '#666' }}
            tickLine={{ stroke: '#ccc' }}
          />
          <YAxis tick={{ fill: '#666' }} tickLine={{ stroke: '#ccc' }} />
          <Tooltip
            itemSorter={(item) => {
              if (typeof item === 'string') {
                return getSeriesOrder(item);
              }

              const seriesName = String(item.value || item.dataKey || '');
              return getSeriesOrder(seriesName);
            }}
            isAnimationActive={false}
            allowEscapeViewBox={{ x: false, y: false }}
            wrapperStyle={{ zIndex: 1000 }}
            contentStyle={{
              backgroundColor: '#ffffff',
              opacity: 1,
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
          />
          <Legend
            itemSorter={(item) => {
              if (typeof item === 'string') {
                return getSeriesOrder(item);
              }

              const seriesName = String(item.value || item.dataKey || '');
              return getSeriesOrder(seriesName);
            }}
            wrapperStyle={{
              paddingTop: '20px',
            }}
            iconType="line"
          />

          {/* PACS conclus - Ligne pointillée verte */}
          <Line
            type="monotone"
            dataKey="conclusion"
            stroke="#21ba45"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#21ba45', r: 4 }}
            name="PACS conclus"
            activeDot={{ r: 6 }}
          />

          {/* PACS modifiés - Ligne pointillée orange */}
          <Line
            type="monotone"
            dataKey="modification"
            stroke="#f2711c"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#f2711c', r: 4 }}
            name="PACS modifiés"
            activeDot={{ r: 6 }}
          />

          {/* PACS dissous - Ligne pointillée rouge */}
          <Line
            type="monotone"
            dataKey="dissous"
            stroke="#db2828"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#db2828', r: 4 }}
            name="PACS dissous"
            activeDot={{ r: 6 }}
          />

          {/* PACS irrecevables - Ligne pointillée violette */}
          <Line
            type="monotone"
            dataKey="irrecevable"
            stroke="#a333c8"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#a333c8', r: 4 }}
            name="PACS irrecevables"
            activeDot={{ r: 6 }}
          />

          {/* Total PACS - Ligne pleine noire */}
          <Line
            type="monotone"
            dataKey="total"
            stroke="#000000"
            strokeWidth={2}
            dot={{ fill: '#000000', r: 4 }}
            name="Total PACS"
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BlocGraphiqueStatistique;
